import { useEffect, useRef } from 'react';

/* Galaxy/orbital starfield background — ported 1:1 from the original
 * assets/js/starfield.js so the animation is pixel-identical to the
 * static build. Respects prefers-reduced-motion (one static frame). */
export default function Starfield() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let stars: Array<{ x: number; y: number; r: number; phase: number; speed: number }> = [];
    let nodes: Array<{ x: number; y: number; vx: number; vy: number }> = [];
    let w = 0, h = 0, dpr = 1;
    let raf = 0;
    let running = true;

    function seed() {
      const count = Math.round((w * h) / 9000);
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * 1.3 + 0.2,
        phase: Math.random() * Math.PI * 2,
        speed: 0.4 + Math.random() * 0.8,
      }));
      const nodeCount = Math.max(5, Math.round(w / 260));
      nodes = Array.from({ length: nodeCount }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.06,
        vy: (Math.random() - 0.5) * 0.06,
      }));
    }

    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas!.width = w * dpr;
      canvas!.height = h * dpr;
      canvas!.style.width = `${w}px`;
      canvas!.style.height = `${h}px`;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
    }

    function isLight() {
      return document.documentElement.getAttribute('data-theme') === 'light';
    }

    function drawStatic() {
      ctx!.clearRect(0, 0, w, h);
      if (isLight()) return;
      ctx!.fillStyle = 'rgba(255,255,255,.7)';
      stars.forEach((s) => {
        ctx!.globalAlpha = 0.5;
        ctx!.beginPath();
        ctx!.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx!.fill();
      });
      ctx!.globalAlpha = 1;
    }

    let t = 0;
    function draw() {
      if (!running) return;
      ctx!.clearRect(0, 0, w, h);
      if (isLight()) {
        raf = requestAnimationFrame(draw);
        return;
      }
      t += 0.016;

      stars.forEach((s) => {
        const a = 0.35 + 0.45 * (0.5 + 0.5 * Math.sin(t * s.speed + s.phase));
        ctx!.globalAlpha = a;
        ctx!.fillStyle = '#f6f8fc';
        ctx!.beginPath();
        ctx!.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx!.fill();
      });
      ctx!.globalAlpha = 1;

      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
      });
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const max = 320;
          if (dist < max) {
            ctx!.strokeStyle = `rgba(0,212,255,${0.14 * (1 - dist / max)})`;
            ctx!.lineWidth = 1;
            ctx!.beginPath();
            ctx!.moveTo(nodes[i].x, nodes[i].y);
            ctx!.lineTo(nodes[j].x, nodes[j].y);
            ctx!.stroke();
          }
        }
      }
      nodes.forEach((n) => {
        ctx!.fillStyle = 'rgba(47,107,255,.55)';
        ctx!.beginPath();
        ctx!.arc(n.x, n.y, 2.2, 0, Math.PI * 2);
        ctx!.fill();
      });

      raf = requestAnimationFrame(draw);
    }

    window.addEventListener('resize', resize);
    resize();
    if (reduceMotion) drawStatic();
    else raf = requestAnimationFrame(draw);

    return () => {
      running = false;
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <>
      <div className="bg-nebula" aria-hidden="true" />
      <canvas className="starfield-canvas" id="starfield" ref={canvasRef} aria-hidden="true" />
    </>
  );
}
